// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  MessageChannel,
  messageTypeCatalogSchema,
  subscriptionsSchema,
} from '@/notification/api/notification-types';
import { z } from 'zod';

const SUBSCRIPTION_URL = '/v1/notifications/subscription';
const MESSAGE_TYPE_URL = '/v1/notifications/message-type';
const ENTITY_WATCH_URL = '/v1/notifications/entity-watch';

const subscriptionsResponseSchema = z.object({
  data: subscriptionsSchema,
  metadata: responseMetadataSchema,
});

const catalogResponseSchema = z.object({
  data: messageTypeCatalogSchema,
  metadata: responseMetadataSchema,
});

export interface SubscriptionInput {
  messageType: string;
  channels: MessageChannel[];
}

export interface SetEntityWatchInput {
  entityType: string;
  entityId: string;
  // The set the caller wants, so an empty one stops the watch
  channels: MessageChannel[];
}

/** Reads every message type the caller can subscribe to. */
export async function getMessageTypes() {
  const response = await api.get(MESSAGE_TYPE_URL);
  const result = await parseResponse(catalogResponseSchema, response.data, {
    schemaName: 'NotificationMessageTypes',
    endpoint: MESSAGE_TYPE_URL,
  });
  return result.data.results;
}

/** Reads the caller's type subscriptions and watched records. */
export async function getSubscriptions() {
  const response = await api.get(SUBSCRIPTION_URL);
  const result = await parseResponse(
    subscriptionsResponseSchema,
    response.data,
    { schemaName: 'NotificationSubscriptions', endpoint: SUBSCRIPTION_URL }
  );
  return result.data;
}

/** Replaces the caller's type subscriptions with the complete set given. */
export async function replaceSubscriptions(
  offeredMessageTypes: string[],
  subscriptions: SubscriptionInput[]
) {
  const response = await api.put(SUBSCRIPTION_URL, {
    // What the screen drew a control for. The save only changes these, so a
    // screen whose catalog never arrived cannot unsubscribe anybody
    offeredMessageTypes,
    subscriptions: subscriptions.map((subscription) => ({
      messageType: subscription.messageType,
      channels: subscription.channels.map(toWireChannel),
    })),
  });
  const result = await parseResponse(
    subscriptionsResponseSchema,
    response.data,
    {
      schemaName: 'ReplaceNotificationSubscriptions',
      endpoint: SUBSCRIPTION_URL,
    }
  );
  return result.data;
}

/** Sets which channels the caller watches one record on, so none stops it. */
export async function setEntityWatch(input: SetEntityWatchInput) {
  const response = await api.put(ENTITY_WATCH_URL, {
    entityType: input.entityType,
    entityId: input.entityId,
    channels: input.channels.map(toWireChannel),
  });
  const result = await parseResponse(
    subscriptionsResponseSchema,
    response.data,
    { schemaName: 'SetEntityWatch', endpoint: ENTITY_WATCH_URL }
  );
  return result.data;
}

// The schema lowercases what the backend sends, and the backend reads back the
// uppercase form it stores
function toWireChannel(channel: MessageChannel): string {
  return channel.toUpperCase();
}
